/**
 * aethereum invite <email> | aethereum invite --link — invite a teammate.
 *
 * POSTs to /api/invites with an account/team token and prints the invite URL (or
 * confirms the email was sent). An accountless (anon) room cannot invite, so we
 * tell the user to 'aethereum claim' first.
 *
 * Fails soft: offline / API-down prints a clear message, exits 1.
 */

import { homedir } from "node:os";
import { AccountClient, OfflineError } from "../hive/account-client.js";
import { getAccount, resolveProject } from "../hive/config-store.js";

export interface InviteOptions {
  /** Force a shareable link instead of an emailed invite. */
  link?: boolean;
  url?: string;
  /** Override the account/team token (else stored login token). */
  token?: string;
  home?: string;
  cwd?: string;
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
}

const DEFAULT_BASE = "https://www.aethereum.dev";

export async function runInvite(email: string | undefined, options: InviteOptions = {}): Promise<void> {
  const home = options.home ?? homedir();
  const cwd = options.cwd ?? process.cwd();
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  const project = resolveProject(cwd, home);
  const account = getAccount(home);

  const base = (options.url ?? env["AETHEREUM_SYNC_API_URL"] ?? account?.url ?? DEFAULT_BASE)
    .replace(/\/$/, "")
    .replace(/^https:\/\/aethereum\.dev(?=$|\/)/, "https://www.aethereum.dev");

  // An anon room with no account behind it cannot create email/link invites, but it
  // CAN be joined with its short code, which is the easy zero-signup path.
  const token = options.token ?? env["AETHEREUM_ACCOUNT_TOKEN"] ?? account?.token;
  if (!token) {
    if (project?.anon && project?.joinCode) {
      console.log(`\n  Bring a teammate into this room, they run:`);
      console.log(`    npx aethereum join ${project.joinCode}\n`);
      console.log(`  Watch it live:  ${base}/dashboard\n`);
      console.log(
        "  Want email/link invites + to keep this room forever? Run 'aethereum claim',\n  then 'aethereum login', then 'aethereum invite'.",
      );
      return;
    }
    if (project?.anon) {
      console.error(
        "This is an accountless room. Claim it first to invite teammates:\n  aethereum claim\nthen 'aethereum login', then 'aethereum invite'.",
      );
    } else {
      console.error("Inviting needs an account. Run 'aethereum login' first, then 'aethereum invite <email>'.");
    }
    process.exit(1);
    return;
  }

  if (!email && !options.link) {
    console.error('Usage: aethereum invite <email>  |  aethereum invite --link');
    process.exit(1);
    return;
  }

  const client = new AccountClient(base, options.fetchImpl ?? fetch);
  try {
    const inviteUrl = await client.createInvite(token, {
      email: options.link ? undefined : email,
      roomId: project?.roomId,
    });
    if (email && !options.link) {
      console.log(`\n  [ok] invite sent to ${email}.`);
      if (inviteUrl) console.log(`       Share link (if email is slow): ${inviteUrl}`);
    } else {
      console.log(`\n  [ok] invite link (anyone with it can join):\n    ${inviteUrl}`);
    }
    if (project?.joinCode) {
      console.log(`\n  Or, the quick CLI way, they run:  npx aethereum join ${project.joinCode}`);
    }
    console.log("");
  } catch (err) {
    if (err instanceof OfflineError) {
      console.error("Could not reach Aethereum (offline?). Try again when you are online.");
    } else {
      console.error(err instanceof Error ? err.message : String(err));
    }
    process.exit(1);
  }
}
